class WigglyScatteredGallery {
  constructor(root) {
    this.root = root;
    this.grid = root.querySelector('[data-gallery-grid]');
    this.loadButton = root.querySelector('[data-gallery-load]');
    this.status = root.querySelector('[data-gallery-status]');
    this.initialCount = Number(root.dataset.initialCount) || 24;
    this.loadCount = Number(root.dataset.loadCount) || 20;
    this.recentWindow = Number(root.dataset.recentWindow) || 6;
    this.rendered = 0;
    this.items = this._readItems();
    this.order = this._scatter(this.items);
    this._bind();
    this._load(this.initialCount);
  }

  _readItems() {
    try {
      const raw = JSON.parse(this.root.querySelector('[data-gallery-items]')?.textContent || '[]');
      const seen = new Set();
      return raw.filter((item) => {
        if (!item?.src || !item?.product_id || seen.has(String(item.media_id))) return false;
        seen.add(String(item.media_id));
        return true;
      });
    } catch (error) {
      console.error('Gallery data could not be read.', error);
      return [];
    }
  }

  _shuffle(items) {
    const result = items.slice();
    for (let index = result.length - 1; index > 0; index -= 1) {
      const swap = Math.floor(Math.random() * (index + 1));
      [result[index], result[swap]] = [result[swap], result[index]];
    }
    return result;
  }

  _scatter(items) {
    const remaining = this._shuffle(items);
    const result = [];
    const recent = [];

    while (remaining.length) {
      let candidates = remaining
        .map((item, index) => ({ item, index }))
        .filter(({ item }) => !recent.includes(String(item.product_id)));

      if (!candidates.length) candidates = remaining.map((item, index) => ({ item, index }));
      const picked = candidates[Math.floor(Math.random() * candidates.length)];
      result.push(picked.item);
      remaining.splice(picked.index, 1);
      recent.push(String(picked.item.product_id));
      if (recent.length > this.recentWindow) recent.shift();
    }
    return result;
  }

  _bind() {
    this.loadButton?.addEventListener('click', () => this._load(this.loadCount));
  }

  _card(item, index) {
    const article = document.createElement('article');
    article.className = 'brand-gallery-card';
    article.dataset.productId = item.product_id;
    article.style.setProperty('--gallery-delay', `${Math.min(index % this.loadCount, 8) * 35}ms`);

    const link = document.createElement('a');
    link.href = item.url;
    link.className = 'brand-gallery-card__link';

    const image = document.createElement('img');
    image.src = item.src;
    image.srcset = item.srcset || '';
    image.sizes = '(max-width: 575px) 46vw, (max-width: 991px) 31vw, 23vw';
    image.alt = item.alt || item.title;
    image.loading = index < this.initialCount ? 'eager' : 'lazy';
    image.decoding = 'async';
    image.width = 900;
    image.height = 900;

    const media = document.createElement('span');
    media.className = 'brand-gallery-card__media';
    media.append(image);

    const footer = document.createElement('span');
    footer.className = 'brand-gallery-card__footer';
    const title = document.createElement('strong');
    title.textContent = item.title;
    const action = document.createElement('span');
    action.textContent = 'View product →';
    footer.append(title, action);

    link.append(media, footer);
    article.append(link);
    return article;
  }

  _load(count) {
    if (!this.grid) return;
    this.grid.querySelector('[data-gallery-loading]')?.remove();
    const next = this.order.slice(this.rendered, this.rendered + count);
    const fragment = document.createDocumentFragment();
    next.forEach((item, offset) => fragment.append(this._card(item, this.rendered + offset)));
    this.grid.append(fragment);
    this.rendered += next.length;
    this.grid.setAttribute('aria-busy', 'false');

    const remaining = Math.max(0, this.order.length - this.rendered);
    if (this.status) this.status.textContent = `${this.rendered} photos shown${remaining ? ` · ${remaining} waiting` : ''}`;
    if (this.loadButton) this.loadButton.hidden = remaining === 0;

    if (window.gsap && !window.matchMedia('(prefers-reduced-motion: reduce)').matches && next.length) {
      const cards = [...this.grid.querySelectorAll('.brand-gallery-card')].slice(-next.length);
      window.gsap.fromTo(cards, { opacity: 0, y: 16 }, { opacity: 1, y: 0, duration: 0.35, stagger: 0.035, ease: 'power2.out', clearProps: 'opacity,transform' });
    }
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-brand-gallery]').forEach((root) => new WigglyScatteredGallery(root));
});
